import { isEqual } from 'lodash-es';
import type React from 'react';
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import * as z from 'zod';

const TYPE_PREFIX = 'nct-databases-';

export enum DragDirection {
  UP = 'up',
  DOWN = 'down',
}

export enum DragType {
  ENTRY_CARD = 'entryCard',
  ENTRY_ROWS = 'entryRows',
  FIELD_COLUMNS = 'fieldColumns',
  FILL_VALUES = 'fillValues',
  SELECT_VALUES = 'selectValues',
}

const dragDataValidator = z.object({
  type: z.nativeEnum(DragType),
  contentId: z.string(),
  dragOverId: z.string(),
  draggingIds: z.array(z.string()),
  height: z.number(),
  width: z.number(),
});

export type DragData = z.infer<typeof dragDataValidator>;

/**
 * Encodes a regular string into a string of character codes encoded as 2-character hex numbers. This is a
 * simple way to encode a string into something case-insensitive that is not using special characters.
 * Should we require a more data-efficient algorithm at some point, base32 might be an option.
 */
function encodeTypeString(str: string): string {
  let result = '';
  for (let i = 0; i < str.length; i++) {
    result += str.charCodeAt(i).toString(16).padStart(2, '0');
  }
  return result;
}

/**
 * Decodes a string encoded by encodeTypeString().
 */
function decodeTypeString(str: string): string {
  let result = '';
  for (let i = 0; i < str.length; i += 2) {
    result += String.fromCharCode(parseInt(str.slice(i, i + 2), 16));
  }
  return result;
}

/**
 * Sets drag data on the given DataTransfer object in a way that can be accessed not just by dragstart and drop
 * events, but also by all other drag events.
 * The DataTransfer.getData() function returns an empty string when used in dragenter and dragover event handlers,
 * see https://stackoverflow.com/a/11193326/242365. The only way to transfer data in a way that is accessible to
 * those handlers is to abuse the transfer "types". This method puts the data into the type rather than the data,
 * by encoding it using encodeTypeString() (because the casing of the type may be changed and special characters
 * may cause problems).
 */
export function setPublicDragData(dataTransfer: DataTransfer, data: DragData): void {
  dataTransfer.setData(`${TYPE_PREFIX}${encodeTypeString(JSON.stringify(data))}`, '');
}

/**
 * Extracts the drag data set by setPublicDragData().
 */
export function getPublicDragData(dataTransfer: DataTransfer): DragData | undefined {
  for (const type of dataTransfer.types) {
    if (type.startsWith(TYPE_PREFIX)) {
      try {
        return dragDataValidator.parse(
          JSON.parse(decodeTypeString(type.slice(TYPE_PREFIX.length)))
        );
      } catch (e: any) {
        // Ignore
      }
    }
  }
}

export type VerticalPositions = { top: number; height: number };
export type HorizontalPositions = { left: number; width: number };
export type DraggingItem = { id?: string; ids?: string[]; idx: number } | undefined;
export type DragOverItem =
  | (DraggingItem & { height?: number; width?: number })
  | undefined;

type Direction = 'vertical' | 'horizontal';
type Position<D> = D extends 'vertical' ? VerticalPositions : HorizontalPositions;

type DraggingReturnProps = {
  dragOver: DragOverItem;
  draggingItem: DraggingItem;
  handleDragEnd: () => void;
  handleDragLeave: () => void;
  handleDrag: (e: React.DragEvent, entryId: string) => void;
  handleDragOver: (e: React.DragEvent) => void;
  handleDragEnter: (e: React.DragEvent) => void;
  handleDrop: (e: React.DragEvent) => void;
};

type DraggingProps<D extends Direction, P extends Position<D>> = {
  dragDirection: D;
  ids: string[];
  rowOrColIds: string[];
  positions: Record<string, P>;
  contentId: string;
  topOrLeftPadding: number;
  dragType: DragType;
  onDrop: (options: DraggingItem) => void;
  onDragOver?: (val: DragOverItem) => void;
  onSetDraggingItem?: (val: DraggingItem) => void;
};

// When starting to drag a card, we hide it in the source column. When dragging a card over a column, we insert
// a gap in its drop destination. Usually when starting to drag a card, its initial drop destination will be in
// the same position on the same card (as that's where the mouse pointer is), so we want to try to seamlessly
// replace the dragged card with a gap. Unfortunately, the "dragStart" event (and the first "drag" event) of the
// dragged card and the first "dragEnter" event of the drop destination board are not triggered at the same time
// but some milliseconds apart from each other. To avoid an animation when the dragged card is removed and
// the drop gap is added a bit later, we synchronize the two events by introducing a bit of a delay in the event
// handlers. We keep all event handlers in the same synchronization queue to make sure they are called in order
// despite the delays.
const dragSync = synchronizeCallbacks();

const DRAG_SYNC_TIMEOUT = 100;
export const useDragItems = <D extends Direction, P extends Position<D>>({
  dragDirection,
  ids,
  rowOrColIds,
  positions,
  contentId,
  onDrop,
  topOrLeftPadding,
  dragType,
  onDragOver,
  onSetDraggingItem,
}: DraggingProps<D, P>): DraggingReturnProps => {
  const [draggingItem, setDraggingItem] = useState<DraggingItem>();
  const [dragOver, setDragOver] = useState<DragOverItem>();
  const dragOverRef = useRef<DragOverItem | null>();
  const handleSetDragOver = useCallback(
    (dragOver: DragOverItem) => {
      setDragOver(dragOver);
      onDragOver?.(dragOver);
    },
    [onDragOver]
  );
  const handleSetDraggingItem = useCallback(
    (draggingItem: DraggingItem) => {
      setDraggingItem(draggingItem);
      onSetDraggingItem?.(draggingItem);
    },
    [onSetDraggingItem]
  );
  useEffect(() => {
    if (draggingItem && draggingItem.id && !ids.includes(draggingItem.id)) {
      handleSetDraggingItem(undefined);
    }
  }, [draggingItem, ids, handleSetDraggingItem]);
  useEffect(() => {
    dragOverRef.current = dragOver;
  }, [dragOver]);

  const getIdx = useCallback(
    (mousePosition: number) => {
      return (
        rowOrColIds.findIndex((rowId) =>
          dragDirection === 'vertical'
            ? (positions[rowId] as VerticalPositions)?.top +
                (positions[rowId] as VerticalPositions)?.height / 2 >
              mousePosition
            : (positions[rowId] as HorizontalPositions)?.left +
                (positions[rowId] as HorizontalPositions)?.width / 2 >
              mousePosition
        ) ?? rowOrColIds.length
      );
    },
    [dragDirection, positions, rowOrColIds]
  );

  // drag event: An entry card in the current board column is a drag source and is being dragged around
  const handleDrag = useCallback(
    (e: React.DragEvent, id: string) => {
      if (draggingItem?.id !== id) {
        dragSync(() => {
          const mousePosition =
            (dragDirection === 'vertical' ? e.clientY : e.clientX) - topOrLeftPadding;
          const idx = getIdx(mousePosition);
          handleSetDraggingItem({ id, idx });
        }, DRAG_SYNC_TIMEOUT); // dragOver event will be triggered on drop destination shortly after
      }
    },
    [dragDirection, draggingItem?.id, getIdx, handleSetDraggingItem, topOrLeftPadding]
  );

  // dragEnd event: An entry card in the current board column is a drag source and the drag operation has ended
  // (meaning that a drop event was fired on the drop destination shortly before)
  const handleDragEnd = useCallback(() => {
    dragSync(() => {
      handleSetDraggingItem(undefined);
    });
  }, [handleSetDraggingItem]);

  // Despite the name "dragEnter" and "dragLeave", the events don't behave like the non-bubbling "mouseEnter" and "mouseLeave"
  // events but rather like the bubbling "mouseOver" and "mouseOut" events, meaning that while dragging around inside the drop
  // destination element, "dragLeave" and "dragEnter" events are continuously fired as the pointer enters and leaves certain
  // descendant nodes of the drop destination element. We simply count these events and can assume that the pointer is within
  // the drop destination element as long as the number of "dragEnter" events exceeds the number of "dragLeave" events.
  // (Fortunately it seems that the "dragEnter" event is called before the "dragLeave" event when moving from one element to
  // another, making event handler delays unnecessary here.)
  const isDragOver = useRef(0);

  // dragOver event: The current board column is a drop destination and a drag operation is being moved around inside it
  // (or one if its descendant nodes).
  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      const data = getPublicDragData(e.dataTransfer);
      e.dataTransfer.dropEffect = 'move';
      if (
        data?.type !== dragType ||
        e.dataTransfer.dropEffect !== 'move' ||
        data.contentId !== contentId
      ) {
        return;
      }

      e.preventDefault();

      const mousePosition =
        (dragDirection === 'vertical' ? e.clientY : e.clientX) - topOrLeftPadding;
      let idx = getIdx(mousePosition);

      if (dragOver && idx > dragOver.idx) {
        idx--;
      }

      const newDragOver: DragOverItem = {
        id: data.dragOverId,
        idx,
        height: dragDirection === 'vertical' ? data.height : undefined,
        width: dragDirection === 'horizontal' ? data.width : undefined,
      };

      if (!isEqual(newDragOver, dragOver)) {
        dragSync(() => {
          handleSetDragOver(newDragOver);
        });
      }
    },
    [
      dragType,
      contentId,
      dragDirection,
      topOrLeftPadding,
      getIdx,
      dragOver,
      handleSetDragOver,
    ]
  );

  // dragEnter event: The current board column is a drop destination and a drag operation has entered it (or one of its
  // descendant nodes).
  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      isDragOver.current++;

      handleDragOver(e);
    },
    [handleDragOver]
  );

  // dragLeave event: The current board column was/is a drop destination and the drag operation has just left it (or one
  // of its descendant nodes).
  const handleDragLeave = useCallback(() => {
    if (--isDragOver.current === 0) {
      dragSync(() => {
        handleSetDragOver(undefined);
      });
    }
  }, [handleSetDragOver]);

  // drop event: The current board column is a drop destination and a drag operation has just dropped an item onto it
  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      isDragOver.current = 0;
      dragSync(() => {
        handleSetDraggingItem(undefined);
        handleSetDragOver(undefined);
      }, DRAG_SYNC_TIMEOUT); // dragEnd event will be triggered on drag source shortly after

      const data = getPublicDragData(e.dataTransfer);
      const currentDragOver = dragOverRef.current;
      if (!currentDragOver || data?.type !== dragType || data.contentId !== contentId) {
        return;
      }

      e.preventDefault();

      dragSync(async () => {
        onDrop({ id: data.dragOverId, idx: currentDragOver.idx, ids: data.draggingIds });
      });
    },
    [contentId, dragType, handleSetDragOver, handleSetDraggingItem, onDrop]
  );

  return useMemo(
    () => ({
      dragOver,
      draggingItem,
      handleDrag,
      handleDragEnd,
      handleDragEnter,
      handleDragLeave,
      handleDragOver,
      handleDrop,
    }),
    [
      dragOver,
      draggingItem,
      handleDrag,
      handleDragEnd,
      handleDragEnter,
      handleDragLeave,
      handleDragOver,
      handleDrop,
    ]
  );
};

/**
 * Delays the execution of the given callbacks by the given timeout and runs all callbacks aggregated during
 * the delay synchronously. This can be used for example when multiple related events (such as a drag
 * event on a draggable element and a dragmove event on the drop target) are expected to be triggered and
 * their handlers should be run synchronously after each other.
 */
function synchronizeCallbacks(): (
  callback: () => void,
  /**
   * The timeout to delay callback execution for, in ms. Only respected for the first callback that is added
   * to a delay queue.
   */
  timeout?: number
) => void {
  let stack: Array<() => void> | undefined = undefined;
  return (callback, timeout = 0) => {
    if (!stack) {
      stack = [];
      setTimeout(() => {
        try {
          for (const cb of stack!) {
            cb();
          }
        } finally {
          stack = undefined;
        }
      }, timeout);
    }

    stack.push(callback);
  };
}
